/**
 * GlobePage - Unified globe-based trip experience.
 *
 * Replaces both TripListPage and TripPage with a single unified view:
 * - Browse mode: Shows TripListOverlay over the globe
 * - Trip-detail mode: Shows TripOverlay with embedded Shoji panels
 *
 * The globe morphs from concave (browse) to flat (trip-detail) based on mode.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { collection, onSnapshot, query } from 'firebase/firestore'

import { auth } from '@/api/firebase'
import { db } from '@/api/firestore'
import { GlobeCanvas } from '@/components/globe'
import { Map } from '@/components/map'
import { darkTheme } from '@/domain/map/styles/themes'
import { GlobeMapProvider } from '@/domain/globe/context'
import { useGlobeMap } from '@/domain/globe/hooks'
import { ToolEffectProvider } from '@/domain/tool-effects'
import { useMapControlsOptional } from '@/components/map'
import {
  TripListOverlay,
  TripOverlay,
  useMapMarkers,
  type TripRow,
} from '@/overlays'
import { createProtoFromFirestore } from '@/lib/proto'
import { getGeocodedDestination } from '@/lib/trip-utils'
import { TripSchema } from '@generated/proto/model'
import { CreateTripDialog } from '@/components/globe/CreateTripDialog'
import { GlobeHeader } from '@/components/globe/GlobeHeader'

import type { Trip as TripMessage } from '@generated/proto/model'

/**
 * Provides tool effect context with map controls.
 * Must be rendered inside the Map component to access map controls via context.
 */
function ToolEffectBridge({ children }: { children: React.ReactNode }) {
  const mapControls = useMapControlsOptional()
  return (
    <ToolEffectProvider value={{ mapControls: mapControls ?? undefined }}>
      {children}
    </ToolEffectProvider>
  )
}

/**
 * Inner component that uses GlobeMapProvider context.
 * Must be rendered inside GlobeMapProvider.
 */
function GlobePageContent() {
  const navigate = useNavigate()
  const params = useParams<{ tripId?: string }>()

  // Trip data from Firestore
  const [trips, setTrips] = useState<TripRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create trip dialog state
  const [createLocation, setCreateLocation] = useState<{ lat: number; lng: number } | null>(null)

  // Globe state and operations
  const { mode, selectedTripId, selectTrip, deselectTrip, setMarkers } = useGlobeMap()

  // Convert trips to markers
  const markers = useMapMarkers(trips)

  // Sync markers to globe state
  useEffect(() => {
    setMarkers(markers)
  }, [markers, setMarkers])

  // Get selected trip object
  const selectedTrip = useMemo(
    () => trips.find((t) => t.id === selectedTripId)?.proto,
    [trips, selectedTripId]
  )

  // Track which tripId we've successfully handled to avoid re-panning
  const handledTripIdRef = useRef<string | null>(null)

  // Handle URL-based trip selection
  // Uses a ref to track handled tripIds instead of selectedTripId to avoid
  // marking as "handled" before the trip data is available
  useEffect(() => {
    // Reset when navigating away from a trip
    if (!params.tripId) {
      handledTripIdRef.current = null
      return
    }

    // Already handled this tripId
    if (params.tripId === handledTripIdRef.current) {
      return
    }

    // Find the trip in loaded data
    const trip = trips.find((t) => t.id === params.tripId)
    if (!trip) {
      // Trip not loaded yet - wait for trips to update
      return
    }

    // Find destination for panning
    const destination = getGeocodedDestination(trip.proto)

    if (destination?.location) {
      selectTrip(params.tripId, {
        latitude: destination.location.latitude,
        longitude: destination.location.longitude,
      })
    } else {
      selectTrip(params.tripId)
    }

    // Mark as handled only after successfully processing
    handledTripIdRef.current = params.tripId
  }, [params.tripId, selectTrip, trips])

  // Firestore subscription
  useEffect(() => {
    const user = auth.currentUser
    if (!user) {
      navigate('/')
      return
    }

    const tripsRef = collection(db, 'users', user.uid, 'trips')
    const unsubscribe = onSnapshot(
      query(tripsRef),
      (snapshot) => {
        try {
          const data = snapshot.docs.map((doc) => {
            const raw = doc.data()
            const proto = createProtoFromFirestore(TripSchema, raw) as TripMessage
            return {
              id: doc.id,
              proto,
              created: raw.created?.toDate?.(),
              modified: raw.modified?.toDate?.(),
            } satisfies TripRow
          })
          setTrips(data)
          setLoading(false)
        } catch (err) {
          console.error('Failed to load trips:', err)
          setError('Failed to load trips')
          setLoading(false)
        }
      },
      (err) => {
        console.error('Firestore subscription failed:', err)
        setError('Failed to connect to database')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [navigate])

  // Handle trip selection from list
  const handleSelectTrip = useCallback(
    (tripId: string, trip: TripMessage) => {
      // Pan to trip's first destination if available
      const destination = getGeocodedDestination(trip)

      if (destination?.location) {
        selectTrip(tripId, {
          latitude: destination.location.latitude,
          longitude: destination.location.longitude,
        })
      } else {
        selectTrip(tripId)
      }

      // Update URL
      navigate(`/trips/${tripId}`, { replace: true })
    },
    [selectTrip, navigate]
  )

  // Handle close trip → return to browse mode
  const handleCloseTrip = useCallback(() => {
    deselectTrip()
    navigate('/trips', { replace: true })
  }, [deselectTrip, navigate])

  // Handle create trip button click
  const handleCreateTrip = useCallback((location?: { latitude: number; longitude: number }) => {
    if (location) {
      setCreateLocation({ lat: location.latitude, lng: location.longitude })
    } else {
      // Default to current center or arbitrary location
      setCreateLocation({ lat: 40, lng: -74 })
    }
  }, [])

  // Handle trip created → navigate to it
  // The URL-based trip selection effect will handle selecting the trip
  // when it appears in the Firestore snapshot
  const handleTripCreated = useCallback(
    (tripId: string) => {
      setCreateLocation(null)
      navigate(`/trips/${tripId}`, { replace: true })
    },
    [navigate]
  )

  // Calculate map center from selected trip's destination
  const mapCenter = useMemo(() => {
    if (!selectedTrip) return { lat: 40, lng: -74 } // Default to NYC
    const destination = getGeocodedDestination(selectedTrip)
    if (destination?.location) {
      return {
        lat: destination.location.latitude,
        lng: destination.location.longitude,
      }
    }
    return { lat: 40, lng: -74 }
  }, [selectedTrip])

  return (
    <main className="relative h-screen w-screen overflow-hidden">
      {/* Background: Globe in browse mode, Google Map in trip-detail mode */}
      {mode === 'browse' ? (
        <GlobeCanvas className="absolute inset-0" />
      ) : (
        <Map
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
          defaultCenter={mapCenter}
          defaultZoom={12}
          className="absolute inset-0"
          options={{ styles: darkTheme.styles }}
        />
      )}

      {/* Minimal header */}
      <GlobeHeader
        showBackButton={mode === 'trip-detail'}
        onBack={handleCloseTrip}
      />

      {/* Error display */}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-md">
          {error}
        </div>
      )}

      {/* Overlays based on mode */}
      {mode === 'browse' && (
        <TripListOverlay
          trips={trips}
          loading={loading}
          onSelectTrip={handleSelectTrip}
          onCreateTrip={handleCreateTrip}
        />
      )}

      {mode === 'trip-detail' && selectedTrip && selectedTripId && (
        <ToolEffectBridge>
          <TripOverlay
            trip={selectedTrip}
            tripId={selectedTripId}
            variant="fullscreen"
            onPlaceSelect={(place) => {
              if (import.meta.env.DEV) {
                console.log('Place selected:', place)
              }
            }}
          />
        </ToolEffectBridge>
      )}

      {/* Create trip dialog */}
      {createLocation && (
        <CreateTripDialog
          location={createLocation}
          onClose={() => setCreateLocation(null)}
          onCreated={handleTripCreated}
        />
      )}
    </main>
  )
}

/**
 * GlobePage - Entry point wrapped with GlobeMapProvider.
 */
export default function GlobePage() {
  return (
    <GlobeMapProvider>
      <GlobePageContent />
    </GlobeMapProvider>
  )
}
